import {
  AssignmentBlockOutputActionType,
  AssignmentBlockOutputCreateModel
} from '@models/assignments';
import * as Sentry from '@sentry/nextjs';
import { useCallback } from 'react';
import {
  clearPendingBlockOutputs,
  loadPendingBlockOutputs,
  savePendingBlockOutputs
} from '../localstorege_handler';
import { EnrichedBlockOutput, useBlockOutputsStore } from '../stores/block_output_logs_store';
import { useImprvApi } from './useImprvApi';

export const useBlockOutputsCollector = () => {
  const api = useImprvApi();

  const drainBlockOutputs = useBlockOutputsStore((state) => state.drainBlockOutputs);
  const setInFlightFlush = useBlockOutputsStore((state) => state.setInFlightFlush);
  const appendBlockOutputs = useBlockOutputsStore((state) => state.appendBlockOutputs);

  const groupOutputsByContext = (outputs: EnrichedBlockOutput[]) => {
    const groups = new Map<string, EnrichedBlockOutput[]>();
    outputs.forEach((blockOutput) => {
      const key = `${blockOutput.nodeEntityId}:${blockOutput.goalSubscriptionId}`;
      if (!groups.has(key)) {
        groups.set(key, []);
      }
      groups.get(key)!.push(blockOutput);
    });
    return groups;
  };

  const flush = useCallback(async () => {
    // Whoever drains the queue takes every caller's logs, so a caller that finds a POST in flight
    // has to wait for it instead of reporting success for logs it never sent.
    let previousPostFailed = false;
    let previousPost = useBlockOutputsStore.getState().inFlightFlush;
    while (previousPost) {
      await previousPost.catch(() => {
        previousPostFailed = true;
      });
      previousPost = useBlockOutputsStore.getState().inFlightFlush;
    }

    const blockOutputs = drainBlockOutputs() as unknown as EnrichedBlockOutput[];
    if (blockOutputs.length === 0) {
      // Nothing left to send and the batch that took our logs failed — they are parked, not saved.
      if (previousPostFailed) {
        throw new Error('Block outputs were not persisted');
      }
      return;
    }

    const post = (async () => {
      const groups = groupOutputsByContext(blockOutputs);
      const failedLogs: EnrichedBlockOutput[] = [];

      await Promise.all(
        Array.from(groups.values()).map(async (groupOutputs) => {
          if (groupOutputs.length === 0) return;

          const { nodeEntityId, goalSubscriptionId } = groupOutputs[0];

          try {
            const sanitizedOutputs = groupOutputs.map(
              ({
                idempotencyKey: _idempotencyKey,
                nodeEntityId: _nodeEntityId,
                goalSubscriptionId: _goalSubscriptionId,
                ...rest
              }) => ({
                ...rest
              })
            );
            await api.createBlockOutputsForNode(
              nodeEntityId,
              goalSubscriptionId,
              sanitizedOutputs as AssignmentBlockOutputCreateModel[],
              true
            );
          } catch (error) {
            Sentry.captureException(error, {
              extra: {
                nodeEntityId,
                goalSubscriptionId
              }
            });
            failedLogs.push(...groupOutputs);
          }
        })
      );

      if (failedLogs.length > 0) {
        savePendingBlockOutputs(failedLogs);
        // Callers gate reads on this promise, so an unsent batch must not resolve as success.
        throw new Error(`Block outputs were not persisted (${failedLogs.length})`);
      }
    })();

    setInFlightFlush(post);
    try {
      await post;
    } finally {
      if (useBlockOutputsStore.getState().inFlightFlush === post) {
        setInFlightFlush(null);
      }
    }
  }, [drainBlockOutputs, setInFlightFlush, api]);

  const retryPendingBlockOutputs = useCallback(async () => {
    const persisted = loadPendingBlockOutputs();
    if (persisted.length === 0) return;

    clearPendingBlockOutputs();

    const groups = groupOutputsByContext(persisted);
    const failedLogs: EnrichedBlockOutput[] = [];

    await Promise.all(
      Array.from(groups.values()).map(async (groupOutputs) => {
        if (groupOutputs.length === 0) return;

        const { nodeEntityId, goalSubscriptionId } = groupOutputs[0];

        try {
          const sanitizedOutputs = groupOutputs.map(
            ({
              idempotencyKey: _idempotencyKey,
              nodeEntityId: _nodeEntityId,
              goalSubscriptionId: _goalSubscriptionId,
              ...rest
            }) => ({
              ...rest
            })
          );
          await api.createBlockOutputsForNode(
            nodeEntityId,
            goalSubscriptionId,
            sanitizedOutputs as AssignmentBlockOutputCreateModel[],
            false
          );
        } catch (error) {
          Sentry.captureException(error, {
            extra: { nodeEntityId, goalSubscriptionId }
          });
          failedLogs.push(...groupOutputs);
        }
      })
    );

    if (failedLogs.length > 0) {
      savePendingBlockOutputs(failedLogs);
    }
  }, [api]);

  const shouldFlush = useCallback((event: AssignmentBlockOutputCreateModel): boolean => {
    return event.actionType === AssignmentBlockOutputActionType.BLOCK_EXITED;
  }, []);

  const logBlockOutput = useCallback(
    (nodeEntityId: string, goalSubscriptionId: number, event: AssignmentBlockOutputCreateModel) => {
      const idempotencyKey = `${nodeEntityId}:${goalSubscriptionId}:${event.actionType}:${event.timestamp}`;
      appendBlockOutputs([
        {
          idempotencyKey,
          nodeEntityId,
          goalSubscriptionId,
          ...event
        } as EnrichedBlockOutput
      ]);

      if (shouldFlush(event)) {
        void flush().catch(() => undefined);
      }
    },
    [appendBlockOutputs, flush, shouldFlush]
  );

  return { logBlockOutput, flush, retryPendingBlockOutputs };
};
